import React from 'react';
import { observer } from 'mobx-react';
import { ParentTable, Status } from '@mq/voltron-parent';
import { Table } from '@mq/voltron-table';
import { LoadingOverlay, VSpacer, Text, Icon, Button, HSpacer } from '@mqd/volt-base';
import PropTypes from 'prop-types';
import BusinessDirectorsTableStore from './BusinessDirectorsTableStore';
import BusinessDirectorDetails from '../business-director-details/BusinessDirectorDetails';

import StyleContext from '../contexts/StyleContext';

class BusinessDirectorsTable extends ParentTable {
  constructor(props) {
    super(props);
    this.storeConstructor = BusinessDirectorsTableStore;
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleDrilldownClose = this.handleDrilldownClose.bind(this);
    this.handleEditButtonClick = this.handleEditButtonClick.bind(this);
    this.handleDirectorEditSave = this.handleDirectorEditSave.bind(this);
    this.state = {
      ...this.colEditState(props, this.directorsTableColumns()),
      activeRow: null,
      loading: false,
      drawerLoading: false,
      editDirectorActive: false,
    };
  }

  get activeRow() {
    return this.store.activeRow && this.store.activeRow.BusinessDirectorStore;
  }

  get canEditDirectorDetails() {
    return this.props.userStore?.canEditBusinessDirectorDetails;
  }

  get canEditDirectorIdentification() {
    return this.props.userStore?.canEditBusinessDirectorIdentification;
  }

  get canRevealIdentification() {
    return this.props.userStore?.canRevealIdentification;
  }

  statusRenderer() {
    return ({ cellValue }) => {
      return <Status status={cellValue === 'true' ? 'Active' : 'Inactive'} />;
    };
  }

  directorsTableColumns() {
    return [
      {
        header: 'Status',
        accessor: 'active',
        key: 'status',
        renderer: this.statusRenderer(),
        primaryKey: true,
      },
      {
        header: 'First Name',
        accessor: 'first_name',
        key: 'first_name',
      },
      {
        header: 'Middle Name',
        accessor: 'middle_name',
        key: 'middle_name',
      },
      {
        header: 'Last Name',
        accessor: 'last_name',
        key: 'last_name',
      },
      {
        header: 'Email',
        accessor: 'email',
        key: 'email',
      },
      {
        header: 'Phone',
        accessor: 'phone',
        key: 'phone',
      },
      {
        header: 'Title',
        accessor: 'title',
        key: 'title',
      },
      {
        header: 'Token',
        accessor: 'token',
        key: 'token',
      },
      {
        header: 'Nationality',
        accessor: 'nationality',
        key: 'nationality',
      },
      {
        header: 'Place of Birth',
        accessor: 'place_of_birth',
        key: 'place_of_birth',
      },
    ];
  }

  handleRowClick({ row: businessDirectorStore }) {
    this.setState({ loading: true });
    businessDirectorStore.getDirectorData(this.store.business_token).then((success) => {
      this.setState({ loading: false });
    });
    this.setState({ activeRow: businessDirectorStore });
    this.store.setAttr('activeRow', businessDirectorStore);
  }

  handleDrilldownClose() {
    this.setState({ activeRow: null, editDirectorActive: false });
    this.store.setAttr('activeRow', null);
  }

  handleEditButtonClick() {
    this.setState({ editDirectorActive: !this.state.editDirectorActive });
  }

  renderDrilldownHeader() {
    const { loading } = this.state;
    const { editDirectorActive } = this.state;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '328px',
          justifyContent: 'space-between',
          marginRight: 4,
        }}
      >
        <Text type="h6">
          {loading ? 'Loading...' : editDirectorActive ? 'Edit Director' : 'Director profile'}
        </Text>
        {this.canEditDirectorDetails && (
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => this.handleEditButtonClick()}
            data-testid="edit-director-icon"
          >
            <Icon type="edit" />
          </div>
        )}
      </div>
    );
  }

  renderDrilldownContent() {
    const { activeRow } = this.store;

    if (activeRow) {
      return (
        <div style={{ width: 336 }}>
          <BusinessDirectorDetails
            businessDirectorStore={activeRow}
            canEditDirectorDetails={this.canEditDirectorDetails}
            canRevealIdentification={this.canRevealIdentification}
            canEditIdentification={this.canEditDirectorIdentification}
            editDirectorActive={this.state.editDirectorActive}
          />
        </div>
      );
    }
  }

  renderDrilldownFooter() {
    const { editDirectorActive } = this.state;

    if (editDirectorActive) {
      return this.renderDrilldownFooterButtons();
    } else {
      return null;
    }
  }

  handleDirectorEditSave() {
    const { activeRow } = this.state;
    this.setState({ loading: true });
    activeRow.updateDirector().then((success) => {
      this.setState({ editDirectorActive: false });
      this.setState({ loading: false });
    });
  }

  renderDrilldownFooterButtons() {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="outline"
          onClick={() => {
            this.setState({ editDirectorActive: false });
            this.setState({ activeRow: null });
          }}
        >
          Cancel
        </Button>
        <HSpacer factor={2} />
        <Button onClick={() => this.handleDirectorEditSave()}>Save</Button>
      </div>
    );
  }

  render() {
    const { loading: detailLoading } = this.props;
    const { currentList, activeSort, setAttr, loading, updateKey } = this.store;
    const { tableKey, columns } = this.state;
    const pageSize = this.store.count;
    const tablePaddingBottom = currentList?.length >= pageSize ? 0 : 31;

    return (
      <div style={{ width: '100%' }} data-testid="business-directors-table-wrapper">
        <VSpacer />
        <StyleContext.Consumer>
          {({ height }) => {
            return (
              <LoadingOverlay active={loading || detailLoading}>
                <Table
                  testId="business-directors-table"
                  key={tableKey}
                  customEmptyState={this.customEmptyState('No directors yet')}
                  loading={loading}
                  height={height + tablePaddingBottom || this.dynamicHeight}
                  data={currentList}
                  columns={columns}
                  sort={activeSort}
                  onSort={(newSort) => setAttr('activeSort', newSort)}
                  scrollToTopUpdateKey={updateKey}
                  handleColSizeChange={this.handleColSizeChange.bind(this)}
                  handleResetTable={this.clearFilterFunc}
                  onRowClick={this.handleRowClick}
                  drilldownHeader={this.renderDrilldownHeader()}
                  drilldownContent={this.renderDrilldownContent()}
                  drilldownFooter={this.renderDrilldownFooter()}
                  onDrilldownCloseClick={this.handleDrilldownClose}
                />
              </LoadingOverlay>
            );
          }}
        </StyleContext.Consumer>
        {this.renderPagination()}
      </div>
    );
  }
}

BusinessDirectorsTable.propTypes = {
  height: PropTypes.number,
  autoHydrate: PropTypes.bool,
  gqlClient: PropTypes.any,
  localStorageKey: PropTypes.string,
  onStoreConstruction: PropTypes.func,
  storeInitArgs: PropTypes.object,
  userStore: PropTypes.object,
  store: PropTypes.object,
};

BusinessDirectorsTable.defaultProps = {
  height: 800,
  autoHydrate: true,
  gqlClient: null,
  localStorageKey: 'BusinessDirectorsTableConfig',
  onStoreConstruction: null,
  storeInitArgs: { limit: 100 },
  store: null,
  userStore: {},
};

export default observer(BusinessDirectorsTable);
